<template>
  <SmallPopup show card-class="w-full max-w-[35rem] bg-popover-solid p-6" @close="emit('close')">
    <!-- keeps the height it opened with on the time range tab, so switching tabs doesn't resize the popup -->
    <div ref="body" class="flex flex-col gap-4" :style="{ minHeight: bodyMinHeight }">
      <div class="flex min-h-8 items-center pr-8 text-title font-bold text-text">
        {{ t('task.Add_manual_time') }}
      </div>

      <ManualTimeAdminRequired v-if="!projectHasWsAdmin" />
      <template v-else>
        <div class="grid grid-cols-2 gap-1 rounded-card bg-input p-1">
          <button
            v-for="tab in TABS"
            :key="tab"
            type="button"
            class="h-10 cursor-pointer rounded-control border-0 text-body font-bold [font-family:inherit]
                   transition-colors duration-150"
            :class="mode === tab ? 'bg-active text-text' : 'bg-transparent text-text-muted hover:text-text'"
            @click="mode = tab"
          >
            {{ t(`task.manual_tab_${tab}`) }}
          </button>
        </div>

        <div v-if="mode === 'range'" class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-body font-bold text-text">
              {{ isToday ? t('task.manual_today') : dayjs(day).format('ddd, D MMM') }}
            </span>
            <span v-if="isToday" class="text-label text-text-muted">{{ dayjs(day).format('ddd, D MMM') }}</span>
            <div class="ml-auto flex gap-2">
              <SimpleButton size="icon" @click="changeDay(-1)">
                <IconChevronLeft16Solid class="size-4" />
              </SimpleButton>
              <SimpleButton size="icon" :disabled="isToday" @click="changeDay(1)">
                <IconChevronRight16Solid class="size-4" />
              </SimpleButton>
            </div>
          </div>

          <div>
            <!-- the padding lets the edge hour labels show in full while farther ones are clipped -->
            <div class="-mx-3 h-7 overflow-hidden px-3">
              <div class="h-full transition-[margin] duration-300" :style="windowStyle">
                <DayTimelineAxis />
              </div>
            </div>
            <div class="h-14 overflow-hidden rounded-control border border-line bg-input">
              <div
                ref="track"
                class="h-full cursor-crosshair touch-none transition-[margin] duration-300 select-none"
                :style="windowStyle"
                @pointerdown="startSelect"
                @pointermove="moveSelect"
              >
                <DayTimelineTrack :spans="spans" :now="now">
                  <div
                    v-if="now"
                    class="absolute inset-y-0 right-0 bg-[repeating-linear-gradient(135deg,var(--line)_0_0.25rem,transparent_0.25rem_0.625rem)]"
                    :style="{ left: dayPercent(maxMinutes) }"
                  >
                    <span class="absolute top-1 left-2 text-unit font-bold text-text uppercase">{{ t('reports.now') }}</span>
                  </div>
                  <div
                    v-if="range"
                    class="pointer-events-none absolute inset-y-1.5 rounded-[0.375rem] border-2 border-accent bg-accent/20"
                    :style="{ left: dayPercent(range.from), width: dayPercent(range.to - range.from) }"
                  >
                    <div
                      v-for="edge in EDGES"
                      :key="edge"
                      class="pointer-events-auto absolute top-1/2 h-6 w-2 -translate-y-1/2 cursor-ew-resize rounded-full bg-accent"
                      :class="edge === 'from' ? '-left-1' : '-right-1'"
                      @pointerdown.stop="grabEdge(edge, $event)"
                    ></div>
                  </div>
                </DayTimelineTrack>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <SimpleButton variant="ghost" size="xs" :disabled="windowStart === 0" @click="windowStart -= WINDOW_STEP_HOURS">
              <IconChevronLeft16Solid class="size-3.5" />
              {{ t('task.manual_earlier') }}
            </SimpleButton>
            <div class="flex gap-4 text-label text-text-muted">
              <span class="flex items-center gap-1.5">
                <span class="size-2.5 rounded-sm bg-accent"></span>{{ t('task.manual_legend_tracked') }}
              </span>
              <span class="flex items-center gap-1.5">
                <span class="size-2.5 rounded-sm border border-accent bg-accent/20"></span>{{ t('task.manual_legend_request') }}
              </span>
            </div>
            <SimpleButton
              variant="ghost"
              size="xs"
              :disabled="windowStart === 24 - WINDOW_HOURS"
              @click="windowStart += WINDOW_STEP_HOURS"
            >
              {{ t('task.manual_later') }}
              <IconChevronRight16Solid class="size-3.5" />
            </SimpleButton>
          </div>

          <div class="flex h-12 items-center gap-3 rounded-control bg-input px-4 tabular-nums">
            <template v-if="range">
              <span class="flex items-center gap-2 text-group font-bold text-text">
                {{ clockTime(range.from) }}
                <IconArrowRight16Solid class="size-4 text-text-muted" />
                {{ clockTime(range.to) }}
              </span>
              <span class="ml-auto min-w-20 rounded-control bg-accent/15 px-3 py-1 text-center font-bold text-accent">
                {{ formatPaddedMinutes(range.to - range.from) }}
              </span>
            </template>
            <span v-else class="text-body text-text-muted">{{ t('task.manual_pick_hint') }}</span>
          </div>
        </div>

        <div v-else class="flex flex-col gap-3">
          <div class="flex flex-col gap-2">
            <div :class="SECTION_TITLE">{{ t('task.manual_how_long') }}</div>
            <div class="grid grid-cols-3 gap-2">
              <SimpleButton
                v-for="preset in PRESETS_MINUTES"
                :key="preset"
                size="md"
                :pressed="durationMinutes === preset"
                :text="formatMinutes(preset)"
                @click="setDuration(preset)"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <div class="text-label text-text-muted">{{ t('task.manual_set_exactly') }}</div>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="field in FIELDS" :key="field.key" class="field-shell h-10 min-w-0 gap-1 px-1">
                <SimpleButton variant="ghost" size="icon" @click="setField(field, duration[field.key] - field.step)">
                  <IconMinus16Solid class="size-4" />
                </SimpleButton>
                <label class="flex min-w-0 flex-1 cursor-text items-baseline justify-center gap-1">
                  <!-- sized to its digits so the number and unit center together -->
                  <input
                    class="field-input max-w-full text-center text-group! font-bold"
                    :style="{ width: `${String(duration[field.key]).length + 1}ch` }"
                    inputmode="numeric"
                    :value="duration[field.key]"
                    @input="onFieldInput(field, $event)"
                  />
                  <span class="text-label text-text-muted">{{ t(field.unit).toLowerCase() }}</span>
                </label>
                <SimpleButton variant="ghost" size="icon" @click="setField(field, duration[field.key] + field.step)">
                  <IconPlus16Solid class="size-4" />
                </SimpleButton>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div :class="SECTION_TITLE">{{ t('task.What_were_you_doing') }}</div>
          <textarea
            v-model="description"
            class="min-h-22 w-full resize-none rounded-control border border-line bg-input p-3 font-[inherit] text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
            :placeholder="t('task.manual_description_placeholder')"
          ></textarea>
        </div>

        <div class="mt-auto flex justify-end gap-2">
          <SimpleButton variant="ghost" :text="t('task.manual_CANCEL')" @click="emit('close')" />
          <!-- fixed width so the label changing during a drag does not shift the buttons; a long duration truncates -->
          <SimpleButton
            variant="accent"
            class="w-40 min-w-0 shrink! [&_.text]:truncate"
            :disabled="!totalMinutes || !description.trim()"
            :text="t('task.manual_request', { duration: formatPaddedMinutes(totalMinutes) })"
            @click="request"
          />
        </div>
      </template>
    </div>
  </SmallPopup>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  IconMinus16Solid, IconPlus16Solid, IconChevronLeft16Solid, IconChevronRight16Solid, IconArrowRight16Solid,
} from '@iconify-prerendered/vue-heroicons';
import SmallPopup from '@/components/elements/SmallPopup';
import SimpleButton from '@/components/elements/SimpleButton';
import DayTimelineAxis from '@/components/elements/DayTimelineAxis.vue';
import DayTimelineTrack from '@/components/elements/DayTimelineTrack.vue';
import ManualTimeAdminRequired from '@/components/widgets/ManualTimeAdminRequired';
import { callAPI } from '@/store/socket';
import { MINUTES_IN_DAY, dayPercent, minutesOfDay } from '@/utils/dayTimeline';
import { formatHoursMinutes, formatHoursPaddedMinutes } from '@/utils/formatDuration';

dayjs.extend(utc);

const props = defineProps({
  taskId: String,
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const SECTION_TITLE = 'text-label font-semibold uppercase tracking-wider text-text-muted';
const TABS = ['range', 'duration'];
const EDGES = ['from', 'to'];
const PRESETS_MINUTES = [15, 30, 45, 60, 90, 120];
const FIELDS = [
  { key: 'hours', unit: 'time.H', step: 1, max: Infinity },
  { key: 'minutes', unit: 'time.M', step: 5, max: 59 },
];
const SNAP_MINUTES = 5;
// the track shows part of the day, scrolled with Earlier / Later
const WINDOW_HOURS = 12;
const WINDOW_STEP_HOURS = 6;

// 'range' picks when the work happened; 'duration' is a bare length that starts now
const mode = ref('range');
const description = ref('');

// range mode
const day = ref(dayjs().format('YYYY-MM-DD'));
const spans = ref([]);
const track = ref(null);
// minutes of `day`: where the drag started and where the pointer is now, in either order
const selection = ref(null);
// the window that holds the current hour, one step back so the hours before it show
const windowStart = ref(Math.min(Math.max((Math.floor(dayjs().hour() / WINDOW_STEP_HOURS) - 1) * WINDOW_STEP_HOURS, 0), 24 - WINDOW_HOURS));

// duration mode
const duration = reactive({ hours: 0, minutes: 0 });

// the app clock ticks every second, so "now" and today follow the time while the dialog is open
const clock = computed(() => store.state.system.nowUTC.local());
const isToday = computed(() => day.value === clock.value.format('YYYY-MM-DD'));
const now = computed(() => (isToday.value ? clock.value.format() : null));
// time after now can't be requested
const maxMinutes = computed(() => (now.value ? minutesOfDay(now.value) : MINUTES_IN_DAY));

// the full-day track is drawn wider than its box and shifted to the window
const windowStyle = computed(() => ({
  width: `${(24 / WINDOW_HOURS) * 100}%`,
  marginLeft: `-${(windowStart.value / WINDOW_HOURS) * 100}%`,
}));

// a click without a drag selects nothing
const range = computed(() => selection.value && selection.value.anchor !== selection.value.at && {
  from: Math.min(selection.value.anchor, selection.value.at),
  to: Math.max(selection.value.anchor, selection.value.at),
});

const durationMinutes = computed(() => duration.hours * 60 + duration.minutes);

const totalMinutes = computed(() => {
  if (mode.value === 'duration') return durationMinutes.value;
  return range.value ? range.value.to - range.value.from : 0;
});

// manual time is reviewed by workspace admins who are approved members of the project
const projectHasWsAdmin = computed(() => store.state.project.project.projectMembers
  .some(pm => pm.approved && store.getters['user/getUser'](pm.userId).userWorkspace.role === 'ADMIN'));

const formatMinutes = (total) => formatHoursMinutes(total * 60, t);
const formatPaddedMinutes = (total) => formatHoursPaddedMinutes(total * 60, t);

// the end of the day reads as this day's 24:00, not the next day's 00:00
const clockTime = (minutes) => (minutes === MINUTES_IN_DAY
  ? '24:00'
  : dayjs(day.value).add(minutes, 'minute').format('HH:mm'));

const clampMinutes = (minutes) => Math.min(Math.max(minutes, 0), maxMinutes.value);

async function loadSpans() {
  spans.value = [];
  selection.value = null;
  const requestedDay = day.value;
  const { report } = await callAPI({
    method: 'workspaceSummaryDayReport',
    // the offset of that day, so a day across a DST change is cut at its own midnight
    params: { date: requestedDay, timezoneOffset: -dayjs(requestedDay).utcOffset() },
  });
  // the day was switched while this answer was on its way
  if (requestedDay !== day.value) return;
  // the report leaves out users with nothing tracked that day
  spans.value = report[store.state.user.user.id] || [];
}

watch(day, loadSpans, { immediate: true });

const body = ref(null);
const bodyMinHeight = ref(null);
// the popup opens on the time range tab, the taller one
onMounted(() => {
  bodyMinHeight.value = `${body.value.offsetHeight}px`;
});

function changeDay(diff) {
  day.value = dayjs(day.value).add(diff, 'day').format('YYYY-MM-DD');
}

function pointerMinutes(event) {
  const rect = track.value.getBoundingClientRect();
  const minutes = ((event.clientX - rect.left) / rect.width) * MINUTES_IN_DAY;
  return clampMinutes(Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES);
}

function startSelect(event) {
  track.value.setPointerCapture(event.pointerId);
  const minutes = pointerMinutes(event);
  selection.value = { anchor: minutes, at: minutes };
}

// dragging a handle re-anchors the selection at its other edge
function grabEdge(edge, event) {
  track.value.setPointerCapture(event.pointerId);
  const other = edge === 'from' ? 'to' : 'from';
  selection.value = { anchor: range.value[other], at: range.value[edge] };
}

// the pointer is captured only while the button is held, so this follows the drag alone
function moveSelect(event) {
  if (!track.value.hasPointerCapture(event.pointerId)) return;
  selection.value.at = pointerMinutes(event);
}

function setDuration(total) {
  duration.hours = Math.floor(total / 60);
  duration.minutes = total % 60;
}

function setField(field, value) {
  duration[field.key] = Math.min(Math.max(value, 0), field.max);
}

// digits only; the input is rewritten so a clamped or stripped value shows what is kept
function onFieldInput(field, event) {
  setField(field, Number(event.target.value.replace(/\D/g, '')));
  event.target.value = duration[field.key];
}

function request() {
  store.dispatch('project/actionAddManualTime', {
    id: window.utils.genId(),
    startDate: mode.value === 'duration' ? dayjs.utc() : dayjs(day.value).add(range.value.from, 'minute').utc(),
    duration: totalMinutes.value * 60,
    description: description.value.trim(),
    status: 'pending',
    taskId: props.taskId,
    userId: store.state.user.user.id,
  });
  emit('close');
}
</script>
